// eslint-disable-next-line
import React, { useState } from 'react';
import './ForgetPassword.css';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Mail } from 'lucide-react';
import LogoImage from '../../../assets/NavbarIcon/logo-image.png';
import Layout from '../../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n/LanguageContext';

const ForgetPassword = () => {
  const [userEmail, setUserEmail] = useState('');
  const [validateEmail, setValidateEmail] = useState(false);
  const [successMessage, setSuccessMesssage] = useState(false);
  const navigate = useNavigate();
  const { translate } = useTranslation();

  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  const handleUserEmailChange = e => {
    setUserEmail(e.target.value);
    setValidateEmail(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!userEmail.trim()) {
      toast.dismiss();
      toast.error(
        translate('forgotPassword', 'validEmailText') || 'Please enter your email address'
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(userEmail)) {
      toast.dismiss();
      toast.error(
        translate('forgotPassword', 'validEmailText') || 'Please enter a valid email address'
      );
      return;
    }

    try {
      const res = await axios.post(`${SERVER_API_URL}/auth/forgot-password`, {
        email: userEmail,
      });
      if (res.status === 200) {
        setValidateEmail(false);
        setSuccessMesssage(true);
      }
    } catch (error) {
      console.log(error);
      setValidateEmail(true);
    }
  };

  const handleBackArrowClick = () => {
    navigate('/');
  };

  const isEmailValid = userEmail.trim() !== '';

  return (
    <Layout title="Klass Online | Forgot Password">
      <Box className="forget-password-container">
        {/* Header Section */}
        <Box className="forget-password-header">
          <Box className="back-button-container">
            <button onClick={handleBackArrowClick} className="back-button">
              <ArrowLeft className="back-icon" />
            </button>
          </Box>

          <Box className="forget-password-logo">
            <img src={LogoImage} alt="Klass Online Logo" className="logo-image" />
          </Box>

          {!successMessage ? (
            <>
              <Typography variant="h3" className="forget-password-title">
                {translate('forgotPassword', 'forgetPasswordText')}
              </Typography>
              <Typography variant="body1" className="forget-password-subtitle">
                {translate('forgotPassword', 'messageText')}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h3" className="forget-password-title">
                {translate('forgotPassword', 'passwordRecoveryText')}
              </Typography>
              <Typography variant="body1" className="forget-password-subtitle">
                {translate('forgotPassword', 'passwordRecoveryMessageText')}
              </Typography>
            </>
          )}
        </Box>

        {/* Main Card */}
        <Box className="forget-password-card">
          {!successMessage ? (
            <form className="forget-password-form" onSubmit={e => handleSubmit(e)}>
              <Box className="fp-input-group">
                <label htmlFor="email" className="fp-input-label">
                  {translate('forgotPassword', 'emailText')}
                </label>
                <Box className="fp-input-wrapper">
                  <Mail className="fp-email-icon" />
                  <input
                    id="email"
                    className="fp-email-input"
                    type="email"
                    placeholder={translate('forgotPassword', 'emailPlaceholder')}
                    value={userEmail}
                    onChange={handleUserEmailChange}
                    required
                  />
                </Box>
                {validateEmail && (
                  <div className="error-message">
                    {translate('forgotPassword', 'validEmailCheckText')}
                  </div>
                )}
              </Box>

              <button
                type="submit"
                className={`primary-button ${!isEmailValid ? 'disabled' : ''}`}
                disabled={!isEmailValid}
              >
                {translate('forgotPassword', 'sendConfirmationText')}
              </button>
            </form>
          ) : (
            <Box className="success-content">
              <Box className="success-icon">
                <Mail className="success-icon-svg" />
              </Box>
              <Typography variant="body1" className="success-message">
                {translate('forgotPassword', 'passwordRecoveryMessageText')}
              </Typography>
              <button onClick={() => navigate('/')} className="primary-button">
                {translate('login', 'backToLogin') || 'Back to Login'}
              </button>
            </Box>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default ForgetPassword;
